import { useState, useEffect } from "react";
import { Mail, Trash2, Download, Upload, Users, MessageSquare, ContactRound } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ContactImportDialog } from '@/components/contacts/ContactImportDialog';
import { ImportedContacts } from '@/components/contacts/ImportedContacts';

const sourceLabel = (s) => (s === "ebook" ? "E-Book" : s === "home" ? "Home" : s ? s : "Home");

const fmt = (d) => {
  if (!d) return "";
  try { return new Date(d).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return d; }
};

const downloadCSV = (filename, rows) => {
  if (!rows.length) { toast.error("Keine Daten zum Export"); return; }
  const headers = Object.keys(rows[0]);
  const esc = (v) => { const value = String(v ?? ""); const safe = /^[\s]*[=+\-@]/.test(value) ? "'" + value : value; return `"${safe.replace(/"/g, '""')}"`; };
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const Contacts = ({ canDelete }) => {
  const [view, setView] = useState("newsletter");
  const [subs, setSubs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = () => {
    api.get("/admin/newsletter").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/admin/contacts").then((r) => setMsgs(r.data)).catch(() => {});
    api.get('/admin/academy-contacts', { params: { page_size: 1 } }).then(r => setImportedCount(r.data.total)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const delSub = async (id) => {
    if (!window.confirm("Diesen Newsletter-Kontakt löschen?")) return;
    await api.delete(`/admin/newsletter/${id}`); setSubs((s) => s.filter((x) => x.id !== id)); toast.success("Gelöscht");
  };
  const delMsg = async (id) => {
    if (!window.confirm("Diese Kontaktanfrage löschen?")) return;
    await api.delete(`/admin/contacts/${id}`); setMsgs((m) => m.filter((x) => x.id !== id)); toast.success("Gelöscht");
  };

  const exportSubs = () => downloadCSV("newsletter-interessenten.csv", subs.map((s) => ({ Name: s.name, Email: s.email, Telefon: s.phone || "", Quelle: sourceLabel(s.source), Sprache: s.lang || "", Datum: fmt(s.created_at) })));
  const exportMsgs = () => downloadCSV("kontaktanfragen.csv", msgs.map((m) => ({ Name: m.name, Email: m.email, Telefon: m.phone || "", Betreff: m.subject || "", Nachricht: m.message, Datum: fmt(m.created_at) })));

  return (
    <div data-testid="contacts-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-4xl uppercase tracking-tight text-white">Kontaktliste</h1>
          <p data-testid="contacts-subtitle" className="text-neutral-500 text-sm mt-1">Newsletter-Interessenten, Kontaktanfragen und importierte Academy-Kontakte.</p>
        </div>
        <div className="flex flex-wrap gap-2">
        {canDelete && <button data-testid="contacts-import-btn" onClick={() => setImportOpen(true)} className="flex items-center gap-2 bg-[#ff0033] hover:bg-[#cc0029] text-white px-4 py-2.5 font-mono-lab text-xs uppercase tracking-widest transition-colors"><Upload size={15} />CSV-Import</button>}
        {view !== 'imported' && <button
          onClick={view === "newsletter" ? exportSubs : exportMsgs}
          data-testid="contacts-export-btn"
          className="flex items-center gap-2 border border-white/15 text-neutral-200 hover:text-[#ff0033] hover:border-[#ff0033] px-4 py-2.5 font-mono-lab text-xs uppercase tracking-widest"
        >
          <Download size={15} /> CSV-Export
        </button>}
        </div>
      </div>

      <div className="flex flex-wrap border border-white/15 w-fit max-w-full mb-6" data-testid="contacts-tabs">
        <button onClick={() => setView("newsletter")} data-testid="tab-newsletter" className={`flex items-center gap-2 px-4 py-2.5 font-mono-lab text-xs uppercase tracking-widest ${view === "newsletter" ? "bg-[#ff0033] text-white" : "text-neutral-400"}`}>
          <Users size={14} /> Newsletter ({subs.length})
        </button>
        <button onClick={() => setView("contacts")} data-testid="tab-contacts" className={`flex items-center gap-2 px-4 py-2.5 font-mono-lab text-xs uppercase tracking-widest ${view === "contacts" ? "bg-[#ff0033] text-white" : "text-neutral-400"}`}>
          <MessageSquare size={14} /> Kontaktanfragen ({msgs.length})
        </button>
        <button onClick={() => setView('imported')} data-testid="tab-imported-contacts" className={`flex items-center gap-2 px-4 py-2.5 font-mono-lab text-xs uppercase tracking-widest ${view === 'imported' ? 'bg-[#ff0033] text-white' : 'text-neutral-400'}`}><ContactRound size={14} />Importierte Kontakte ({importedCount})</button>
      </div>

      {view === 'imported' && <ImportedContacts canDelete={canDelete} refreshKey={refreshKey} onChange={load} />}
      {importOpen && <ContactImportDialog onClose={() => setImportOpen(false)} onImported={() => { setView('imported'); setRefreshKey(k => k+1); load(); }} />}

      {view === "newsletter" && (
        <div className="space-y-2" data-testid="newsletter-list">
          {subs.length === 0 && <p className="text-neutral-500 text-sm">Noch keine Newsletter-Anmeldungen.</p>}
          {subs.map((s) => (
            <div key={s.id} className="bg-[#0F0F0F] border border-white/10 p-4 flex items-center gap-4" data-testid={`newsletter-row-${s.id}`}>
              <div className="w-9 h-9 shrink-0 border border-[#ff0033]/40 text-[#ff0033] flex items-center justify-center"><Mail size={16} /></div>
              <div className="min-w-0 flex-1">
                <p className="text-white truncate">{s.name || "—"} <a href={`mailto:${s.email}`} className="text-neutral-400 hover:text-[#ff0033]">· {s.email}</a></p>
                <p className="text-neutral-500 text-xs">{s.phone ? s.phone + " · " : ""}{fmt(s.created_at)}</p>
              </div>
              <span className={`shrink-0 font-mono-lab text-[10px] uppercase tracking-widest px-2 py-1 border ${s.source === "ebook" ? "border-amber-500/40 text-amber-400" : "border-white/20 text-neutral-400"}`}>{sourceLabel(s.source)}</span>
              {canDelete && <button onClick={() => delSub(s.id)} data-testid={`del-newsletter-${s.id}`} className="p-2 text-neutral-500 hover:text-[#ff0033] shrink-0"><Trash2 size={16} /></button>}
            </div>
          ))}
        </div>
      )}

      {view === "contacts" && (
        <div className="space-y-2" data-testid="contacts-list">
          {msgs.length === 0 && <p className="text-neutral-500 text-sm">Noch keine Kontaktanfragen.</p>}
          {msgs.map((m) => (
            <div key={m.id} className="bg-[#0F0F0F] border border-white/10 p-4 flex items-start gap-4" data-testid={`contact-row-${m.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-white">{m.name} <a href={`mailto:${m.email}`} className="text-neutral-400 hover:text-[#ff0033]">· {m.email}</a> {m.phone && <span className="text-neutral-500 text-sm">· {m.phone}</span>}</p>
                {m.subject && <p className="text-[#ff0033] text-xs uppercase tracking-wide mt-1">{m.subject}</p>}
                <p className="text-neutral-400 text-sm mt-1 whitespace-pre-line">{m.message}</p>
                <p className="text-neutral-600 text-[11px] mt-2">{fmt(m.created_at)}</p>
              </div>
              {canDelete && <button onClick={() => delMsg(m.id)} data-testid={`del-contact-${m.id}`} className="p-2 text-neutral-500 hover:text-[#ff0033] shrink-0"><Trash2 size={16} /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Contacts;
